/**
 * Sales — origination performance: targets vs actuals per region, the
 * agent leaderboard, and the repeat-borrower retention metric.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Target } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/lendingCalculations";
import { staffApi } from "@/platform/staffApi";
import { useStaff } from "./StaffShell";

type Row = Record<string, any>;

const thisMonth = () => new Date().toISOString().slice(0, 7);

export function SalesPage() {
  const staff = useStaff();
  const { toast } = useToast();
  const isManager = ["regional_manager", "national_manager", "admin"].includes(staff?.role || "");

  const [period, setPeriod] = useState(thisMonth());
  const [sales, setSales] = useState<Row | null>(null);
  const [agents, setAgents] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true);
  const [targetFor, setTargetFor] = useState<Row | null>(null);
  const [targetValue, setTargetValue] = useState("");
  const [targetCount, setTargetCount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        staffApi.salesPerformance(period),
        staffApi.agents(period),
      ]);
      setSales(s);
      setAgents(a);
    } catch (error) {
      toast({ title: "Could not load sales", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [period, toast]);

  useEffect(() => { load(); }, [load]);

  const saveTarget = async () => {
    if (!targetFor) return;
    setBusy(true);
    try {
      await staffApi.setTarget({
        period, region_id: targetFor.region_id,
        target_value: Number(targetValue), target_count: Number(targetCount || 0),
      });
      toast({ title: `Target set for ${targetFor.name}` });
      setTargetFor(null); setTargetValue(""); setTargetCount("");
      load();
    } catch (error) {
      toast({ title: "Could not save target", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally { setBusy(false); }
  };

  if (loading) return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin" />;

  const totalDisbursed = (sales?.regions || []).reduce((s: number, r: Row) => s + r.disbursed_value, 0);
  const totalTarget = (sales?.regions || []).reduce((s: number, r: Row) => s + (r.target_value || 0), 0);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Sales</h1>
          <p className="text-sm text-muted-foreground">
            Disbursement performance against target, and who is originating it.
          </p>
        </div>
        <div className="w-40">
          <Label className="text-xs">Period</Label>
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground">Disbursed this period</p>
            <p className="mt-1 text-2xl font-bold tabular-nums">{formatCurrency(totalDisbursed)}</p>
            {totalTarget > 0 && (
              <p className="text-xs text-muted-foreground">
                {((totalDisbursed / totalTarget) * 100).toFixed(0)}% of {formatCurrency(totalTarget)} target
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground">Loans disbursed</p>
            <p className="mt-1 text-2xl font-bold tabular-nums">
              {(sales?.regions || []).reduce((s: number, r: Row) => s + r.disbursed_count, 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground">Repeat borrowers</p>
            <p className="mt-1 text-2xl font-bold tabular-nums">
              {agents?.repeat_borrower_rate == null ? "—" : `${agents.repeat_borrower_rate}%`}
            </p>
            <p className="text-xs text-muted-foreground">of all disbursements</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Target vs actual by region</CardTitle>
          <CardDescription>Set a monthly disbursement target for each region.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(sales?.regions || []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No regions configured yet.</p>
          ) : (
            sales!.regions.map((r: Row) => (
              <div key={r.region_id}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{r.name}</span>
                  <span className="tabular-nums">
                    {formatCurrency(r.disbursed_value)}
                    {r.target_value ? (
                      <span className="text-muted-foreground"> of {formatCurrency(r.target_value)}</span>
                    ) : (
                      <span className="text-muted-foreground"> · no target</span>
                    )}
                    {isManager && (
                      <Button size="sm" variant="link" className="ml-2 h-auto p-0"
                        onClick={() => {
                          setTargetFor(r);
                          setTargetValue(r.target_value ? String(r.target_value) : "");
                          setTargetCount(r.target_count ? String(r.target_count) : "");
                        }}>
                        <Target className="mr-1 h-3 w-3" />set
                      </Button>
                    )}
                  </span>
                </div>
                <Progress className="mt-1 h-2"
                  value={r.attainment == null ? 0 : Math.min(r.attainment, 100)} />
                {r.attainment != null && (
                  <p className={`mt-1 text-xs ${r.attainment >= 100 ? "text-success" : "text-muted-foreground"}`}>
                    {r.attainment}% of target · {r.disbursed_count} loans
                  </p>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Agent leaderboard</CardTitle>
          <CardDescription>Who decided and originated this period's business</CardDescription>
        </CardHeader>
        <CardContent>
          {(agents?.agents || []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No decisions recorded this period yet.
            </p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Staff</TableHead>
                <TableHead>Decided</TableHead>
                <TableHead>Approval rate</TableHead>
                <TableHead>Disbursed</TableHead>
                <TableHead>Avg ticket</TableHead>
                <TableHead>Repeat</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {agents!.agents.map((a: Row) => (
                  <TableRow key={a.staff_id}>
                    <TableCell>
                      <p className="font-medium">{a.name || "—"}</p>
                      <p className="text-xs capitalize text-muted-foreground">
                        {String(a.role || "").split("_").join(" ")}
                      </p>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {a.decided}
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({a.approved} ok / {a.rejected} no)
                      </span>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {a.approval_rate == null ? "—" : `${a.approval_rate}%`}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {formatCurrency(a.disbursed_value)}
                      <span className="ml-1 text-xs text-muted-foreground">({a.disbursed_count})</span>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {a.average_ticket ? formatCurrency(a.average_ticket) : "—"}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {a.repeat_rate == null ? "—" : `${a.repeat_rate}%`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(targetFor)} onOpenChange={(open) => !open && setTargetFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Target for {targetFor?.name} · {period}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Value (K)</Label>
              <Input inputMode="decimal" value={targetValue}
                onChange={(e) => setTargetValue(e.target.value.replace(/[^\d.]/g, ""))} />
            </div>
            <div>
              <Label>Loan count</Label>
              <Input inputMode="numeric" value={targetCount}
                onChange={(e) => setTargetCount(e.target.value.replace(/\D/g, ""))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTargetFor(null)}>Cancel</Button>
            <Button onClick={saveTarget} disabled={busy || !Number(targetValue)}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save target"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
